import graphql__c965adf1f0b422e82572 from "./__generated__/FormFieldOptionsSelect_answer.graphql.js";import { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import uniqBy from "lodash/uniqBy";

import {
  searchFormFieldOptions,
  SEARCH_DEBOUNCE_TIMEOUT
} from "./searchFormFieldOptions";
import Select from "../Select/Select";
import AsyncPaginateSelect from "../Select/AsyncPaginateSelect";
import { useFormatMessage } from "../Intl/Context";

// Kept out of the component so the default doesn't change identity between renders
const NO_OPTIONS = [];

// The select of a field offering options to choose from, either listing them, or
// searching them when the field holds too many options to be listed with the form.
// Searched options are loaded page by page, as the contributor scrolls through the menu.
const FormFieldOptionsSelect = ({
  answer,
  options,
  storedOptions = NO_OPTIONS,
  otherField,
  selectedValues,
  onOptionsChanged,
  isMulti,
  ...selectProps
}) => {
  const { selectedOptions, formField } = useFragment(
    FormFieldOptionsSelect_answer,
    answer
  );
  const { id: formFieldId, optionsSearchable } = formField;
  const t = useFormatMessage();
  const [pickedOptions, setPickedOptions] = useState([]);

  const answerOptions = useMemo(
    () => selectedOptions.map(({ id, name }) => ({ value: id, label: name })),
    [selectedOptions]
  );

  // Where the labels of the selected ids are looked up : the options listed with the
  // form, the ones the saved answer points at, the one the form store kept, and the ones
  // picked here, as a searched menu forgets its results as soon as it closes.
  const displayableOptions = useMemo(() => {
    const otherOptions = otherField ? [otherField] : [];

    return [
      ...options,
      ...answerOptions,
      ...storedOptions,
      ...otherOptions,
      ...pickedOptions
    ];
  }, [options, answerOptions, storedOptions, otherField, pickedOptions]);

  // The react-select components accept a { value, label } option object as their value
  // prop, so the selected ids are given back the options they were chosen from.
  const value = useMemo(() => {
    const selectedOptions = selectedValues
      .map(selectedValue =>
        displayableOptions.find(option => option.value === selectedValue)
      )
      .filter(Boolean);

    return isMulti ? selectedOptions : selectedOptions[0] || null;
  }, [selectedValues, displayableOptions, isMulti]);

  const handleChange = useCallback(
    changedOptions => {
      const pickedOptions = [changedOptions].flat().filter(Boolean);

      if (optionsSearchable) {
        setPickedOptions(current => uniqBy([...current, ...pickedOptions], "value"));
      }

      onOptionsChanged(pickedOptions);
    },
    [optionsSearchable, onOptionsChanged]
  );

  const loadOptions = useCallback(
    (terms, _loadedOptions, { cursor } = {}) =>
      searchFormFieldOptions({ formFieldId, terms, cursor, otherField }),
    [formFieldId, otherField]
  );

  const listedOptions = useMemo(() => {
    const otherOptions = otherField ? [otherField] : [];

    return [...options, ...otherOptions];
  }, [options, otherField]);

  if (!optionsSearchable) {
    return (
      <Select
        value={value}
        options={listedOptions}
        onChange={handleChange}
        isMulti={isMulti}
        {...selectProps}
      />
    );
  }

  const searchPlaceholder = t(intlMessages.searchPlaceholder);

  return (
    <AsyncPaginateSelect
      value={value}
      loadOptions={loadOptions}
      additional={{ cursor: null }}
      debounceTimeout={SEARCH_DEBOUNCE_TIMEOUT}
      onChange={handleChange}
      isMulti={isMulti}
      {...selectProps}
      // Placed after the caller's own props : nothing else tells the contributor that
      // the menu holds more than its first options, and that typing narrows them down
      placeholder={searchPlaceholder}
    />
  );
};

const FormFieldOptionsSelect_answer = graphql__c965adf1f0b422e82572;

export default FormFieldOptionsSelect;

const intlMessages = defineMessages({
  searchPlaceholder: {
    id: "input.formFieldOptionsSearch.placeholder",
    defaultMessage: "Saisissez pour rechercher une option ..."
  }
});
